/**
 * Frozen Phase 11 contract: trusted service-peer WhatsApp reads only.
 * APP JWT/SID and all write paths retain their existing admission.
 */
export const WHATSAPP_READ_GRANT_TTL_MS = 120_000;
export type WhatsAppReadAudience =
  | "email-executor" | "files-executor" | "people-tasks-executor"
  | "calendar-executor" | "contact-executor" | "expansion-executor"
  | "creator-connections";
export interface WhatsAppDatabaseReadOrigin {
 readonly owner_id:string; readonly session_id:string; readonly session_created_at:string;
 readonly link_id:string; readonly link_revision:string; readonly link_created_at:string;
 readonly channel_read_id:string; readonly issued_at_ms:number; readonly expires_at_ms:number;
}
export interface MirrorWhatsAppReadIdentity {
 readonly databaseOrigin?: WhatsAppDatabaseReadOrigin;
  readonly ownerId: string;
  readonly sessionId: string;
  readonly channelReadId: string;
  readonly authSessionId: null;
  /** Fresh owned active-link revision + WhatsApp-session checks. Fail closed. */
  verify(): Promise<boolean>;
}
export interface MirrorWhatsAppReadContext extends MirrorWhatsAppReadIdentity {
  /** Closed read action only. Adds derived user_id + channel_read_grant.
   * Hash covers the full signed body (except channel_read_grant), including user_id.
   * Null means revoked, expired, invalid payload or non-read operation.
   */
  bindReadBody(audience: WhatsAppReadAudience, body: Record<string, unknown>): Promise<Record<string, unknown> | null>;
}
export interface AdmittedMirrorWhatsAppReadGrant extends MirrorWhatsAppReadIdentity {
  /** Original body with channel_read_grant/user_id removed for strict parsers. */
  readonly body: Record<string, unknown>;
}
export interface CreateMirrorWhatsAppReadContextOptions {
  db: any;
  authorization: string;
  serviceRoleKey: string;
  secret: string;
  userId: string;
  sessionId: string;
  linkId: string;
  now?: () => number;
}
export interface AdmitMirrorWhatsAppReadGrantOptions {
  request: Request;
  body: Record<string, unknown>;
  audience: WhatsAppReadAudience;
  db: any;
  serviceRoleKey: string;
  secret: string;
  now?: () => number;
}
// Implementation follows this frozen signature; no database writes here.
// createMirrorWhatsAppReadContext(options): Promise<MirrorWhatsAppReadContext | null>
// admitMirrorWhatsAppReadGrant(options): Promise<AdmittedMirrorWhatsAppReadGrant | null>

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const READ_ACTIONS: Record<WhatsAppReadAudience, readonly string[]> = {
  "email-executor": ["probe", "search", "get", "thread", "attachment"],
  "files-executor": ["search", "get"],
  "people-tasks-executor": ["probe", "lists", "search", "get"],
  "calendar-executor": ["list_events", "get_event", "availability"],
  "contact-executor": ["search_contacts", "get_contact"],
  "expansion-executor": ["lists", "search", "get", "thread"],
  "creator-connections": ["balance", "lists", "video_status"],
};
interface ReadClaims {
  version: 1; channel: "whatsapp"; owner_id: string; session_id: string;
  session_created_at: string; link_id: string; link_revision: string;
  link_created_at: string; channel_read_id: string; issued_at_ms: number;
  expires_at_ms: number; audience: WhatsAppReadAudience; operation: string;
  body_sha256: string;
}
interface OriginSnapshot {
  ownerId: string; sessionId: string; sessionCreatedAt: string;
  linkId: string; linkRevision: string; linkCreatedAt: string;
}
const encoder = new TextEncoder();
function servicePeer(authorization: string, serviceKey: string): boolean {
  if (!serviceKey || serviceKey.length < 16) return false;
  const a = encoder.encode(authorization), b = encoder.encode("Bearer " + serviceKey);
  let difference = a.length ^ b.length;
  for (let i = 0; i < Math.max(a.length, b.length); i++) difference |= (a[i] || 0) ^ (b[i] || 0);
  return difference === 0;
}
function canonical(value: unknown, depth = 0): string {
  if (depth > 20) throw new Error("read payload nesting");
  if (value === null || typeof value === "boolean" || typeof value === "string") return JSON.stringify(value);
  if (typeof value === "number" && Number.isFinite(value)) return JSON.stringify(value);
  if (Array.isArray(value)) return "[" + value.map(item => canonical(item, depth + 1)).join(",") + "]";
  if (typeof value !== "object" || !value) throw new Error("read payload type");
  const object = value as Record<string, unknown>;
  const keys = Object.keys(object).sort();
  if (keys.some(key => ["__proto__", "constructor", "prototype"].includes(key))) throw new Error("read payload key");
  return "{" + keys.map(key => JSON.stringify(key) + ":" + canonical(object[key], depth + 1)).join(",") + "}";
}
function base64url(bytes: Uint8Array): string {
  let chars = "";
  for (const byte of bytes) chars += String.fromCharCode(byte);
  return btoa(chars).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}
function decodeSignature(value: unknown): Uint8Array | null {
  if (typeof value !== "string" || !/^[A-Za-z0-9_-]{43}$/.test(value)) return null;
  try { return Uint8Array.from(atob(value.replace(/-/g, "+").replace(/_/g, "/") + "="), char => char.charCodeAt(0)); }
  catch { return null; }
}
async function digest(body: Record<string, unknown>): Promise<string> {
  const bytes = encoder.encode(canonical(body));
  if (bytes.byteLength > 65_536) throw new Error("read payload size");
  const result = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
  return Array.from(result, byte => byte.toString(16).padStart(2, "0")).join("");
}
async function keyFor(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", encoder.encode(secret), { name: "HMAC", hash: "SHA-256" }, false, ["sign", "verify"]);
}
function record(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}
function operationFor(audience: WhatsAppReadAudience, body: Record<string, unknown>): string | null {
  const action = body.action;
  if (typeof action !== "string" || !READ_ACTIONS[audience]?.includes(action)) return null;
  // A read grant can never smuggle a reviewed write / approval receipt.
  if (Object.keys(body).some(key => /^(approval|approved|receipt|review|write|execute|proposal)(_|$)/i.test(key))) return null;
  if (audience === "email-executor" && action === "attachment" && body.format !== "text") return null;
  return action;
}
async function snapshot(db: any, ownerId: string, sessionId: string, linkId: string): Promise<OriginSnapshot | null> {
  try {
    const [linkReply, sessionReply] = await Promise.all([
      db.from("whatsapp_links").select("id,user_id,active,created_at,read_revision").eq("id", linkId).eq("user_id", ownerId).eq("active", true).maybeSingle(),
      db.from("mirror_sessions").select("id,user_id,kind,created_at").eq("id", sessionId).eq("user_id", ownerId).eq("kind", "whatsapp").maybeSingle(),
    ]);
    const link = linkReply.data, session = sessionReply.data;
    if (linkReply.error || sessionReply.error || !link || !session || link.id !== linkId
      || link.user_id !== ownerId || link.active !== true || !UUID.test(link.read_revision || "")
      || typeof link.created_at !== "string" || !Number.isFinite(Date.parse(link.created_at))
      || session.id !== sessionId || session.user_id !== ownerId || session.kind !== "whatsapp"
      || typeof session.created_at !== "string" || !Number.isFinite(Date.parse(session.created_at))) return null;
    return { ownerId, sessionId, sessionCreatedAt: session.created_at, linkId, linkRevision: link.read_revision, linkCreatedAt: link.created_at };
  } catch { return null; }
}
function sameOrigin(a: OriginSnapshot, b: OriginSnapshot | null): boolean {
  return !!b && Object.keys(a).every(key => a[key as keyof OriginSnapshot] === b[key as keyof OriginSnapshot]);
}
function claimsValid(claims: unknown, audience: WhatsAppReadAudience, now: number): claims is ReadClaims {
  if (!record(claims)) return false;
  const keys = ["version", "channel", "owner_id", "session_id", "session_created_at", "link_id", "link_revision", "link_created_at", "channel_read_id", "issued_at_ms", "expires_at_ms", "audience", "operation", "body_sha256"];
  if (Object.keys(claims).length !== keys.length || keys.some(key => !Object.hasOwn(claims, key))) return false;
  if (claims.version !== 1 || claims.channel !== "whatsapp" || claims.audience !== audience
    || !["owner_id", "session_id", "link_id", "link_revision", "channel_read_id"].every(key => typeof claims[key] === "string" && UUID.test(claims[key] as string))
    || !["session_created_at", "link_created_at"].every(key => typeof claims[key] === "string" && Number.isFinite(Date.parse(claims[key] as string)))
    || typeof claims.operation !== "string" || !READ_ACTIONS[audience]?.includes(claims.operation)
    || typeof claims.body_sha256 !== "string" || !/^[a-f0-9]{64}$/.test(claims.body_sha256)
    || typeof claims.issued_at_ms !== "number" || !Number.isSafeInteger(claims.issued_at_ms)
    || typeof claims.expires_at_ms !== "number" || !Number.isSafeInteger(claims.expires_at_ms)
    || !Number.isFinite(now) || claims.issued_at_ms > now + 5_000
    || claims.expires_at_ms <= now || claims.expires_at_ms <= claims.issued_at_ms
    || claims.expires_at_ms - claims.issued_at_ms > WHATSAPP_READ_GRANT_TTL_MS) return false;
  return true;
}
export async function createMirrorWhatsAppReadContext(options: CreateMirrorWhatsAppReadContextOptions): Promise<MirrorWhatsAppReadContext | null> {
  const { db, userId, sessionId, linkId, secret } = options;
  const now = options.now ?? Date.now;
  if (!servicePeer(options.authorization, options.serviceRoleKey) || secret.length < 32
    || ![userId, sessionId, linkId].every(value => UUID.test(value))) return null;
  const issuedAt = now();
  if (!Number.isSafeInteger(issuedAt)) return null;
  const origin = await snapshot(db, userId, sessionId, linkId);
  if (!origin) return null;
  const channelReadId = crypto.randomUUID(), expiresAt = issuedAt + WHATSAPP_READ_GRANT_TTL_MS;
  const verify = async () => now() < expiresAt && sameOrigin(origin, await snapshot(db, userId, sessionId, linkId));
  return {
    ownerId: userId, sessionId, channelReadId, authSessionId: null, verify,
    databaseOrigin: Object.freeze({owner_id:userId,session_id:sessionId,session_created_at:origin.sessionCreatedAt,link_id:linkId,link_revision:origin.linkRevision,link_created_at:origin.linkCreatedAt,channel_read_id:channelReadId,issued_at_ms:issuedAt,expires_at_ms:expiresAt}),
    async bindReadBody(audience, body) {
      try {
        if (!record(body) || Object.hasOwn(body, "channel_read_grant")
          || (Object.hasOwn(body, "user_id") && body.user_id !== userId)
          || (Object.hasOwn(body, "expected_owner_id") && body.expected_owner_id !== userId)
          || (Object.hasOwn(body, "session_id") && body.session_id !== sessionId)
          || !await verify()) return null;
        const signedBody = { ...body, user_id: userId }, operation = operationFor(audience, signedBody);
        if (!operation) return null;
        const claims: ReadClaims = {
          version: 1, channel: "whatsapp", owner_id: userId, session_id: sessionId,
          session_created_at: origin.sessionCreatedAt, link_id: linkId, link_revision: origin.linkRevision,
          link_created_at: origin.linkCreatedAt, channel_read_id: channelReadId,
          issued_at_ms: issuedAt, expires_at_ms: expiresAt, audience, operation,
          body_sha256: await digest(signedBody),
        };
        const signature = base64url(new Uint8Array(await crypto.subtle.sign("HMAC", await keyFor(secret), encoder.encode(canonical(claims)))));
        if (!await verify()) return null;
        return { ...signedBody, channel_read_grant: { claims, signature } };
      } catch { return null; }
    },
  };
}
export async function admitMirrorWhatsAppReadGrant(options: AdmitMirrorWhatsAppReadGrantOptions): Promise<AdmittedMirrorWhatsAppReadGrant | null> {
  const { db, audience, secret, request } = options;
  const now = options.now ?? Date.now;
  if (!servicePeer(request.headers.get("authorization") || "", options.serviceRoleKey) || secret.length < 32 || !record(options.body)) return null;
  try {
    const token = options.body.channel_read_grant;
    if (!record(token) || Object.keys(token).length !== 2 || !Object.hasOwn(token, "claims") || !Object.hasOwn(token, "signature")) return null;
    const claims = token.claims, signature = decodeSignature(token.signature);
    if (!signature || !claimsValid(claims, audience, now())
      || options.body.user_id !== claims.owner_id
      || (Object.hasOwn(options.body, "expected_owner_id") && options.body.expected_owner_id !== claims.owner_id)) return null;
    const { channel_read_grant: _grant, ...signedBody } = options.body;
    if (operationFor(audience, signedBody) !== claims.operation || await digest(signedBody) !== claims.body_sha256
      || !await crypto.subtle.verify("HMAC", await keyFor(secret), signature, encoder.encode(canonical(claims)))) return null;
    const expected: OriginSnapshot = { ownerId: claims.owner_id, sessionId: claims.session_id,
      sessionCreatedAt: claims.session_created_at, linkId: claims.link_id,
      linkRevision: claims.link_revision, linkCreatedAt: claims.link_created_at };
    const verify = async () => claimsValid(claims, audience, now())
      && sameOrigin(expected, await snapshot(db, claims.owner_id, claims.session_id, claims.link_id));
    if (!await verify()) return null;
    const { user_id: _owner, ...body } = signedBody;
    return { ownerId: claims.owner_id, sessionId: claims.session_id,
      channelReadId: claims.channel_read_id, authSessionId: null, body, verify,
      databaseOrigin: Object.freeze({owner_id:claims.owner_id,session_id:claims.session_id,session_created_at:claims.session_created_at,link_id:claims.link_id,link_revision:claims.link_revision,link_created_at:claims.link_created_at,channel_read_id:claims.channel_read_id,issued_at_ms:claims.issued_at_ms,expires_at_ms:claims.expires_at_ms}) };
  } catch { return null; }
}
